'use client';

import { useState, useLayoutEffect, useRef } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { DateRange } from 'react-day-picker';
import { CalendarDays } from 'lucide-react';
import { cn } from '@/lib/utils';
import { normalizeDateRange } from './scheduleBlockUtils';

/** Approx. width per month (days 7*32 + caption + padding). */
const MIN_WIDTH_PER_MONTH = 280;
const GAP_PX = 16;

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

const DATE_WITH_WEEKDAY_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  ...DATE_FORMAT,
};

function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString('es-AR', options);
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Calculates how many months fit in the container width without overflow.
 * Always at least 2; if 4 don't fit, we show 3 and bands remain on the sides.
 */
function useNumberOfMonths(containerRef: React.RefObject<HTMLDivElement | null>): number {
  const [n, setN] = useState(2);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      if (w >= MIN_WIDTH_PER_MONTH * 4 + GAP_PX * 3) setN(4);
      else if (w >= MIN_WIDTH_PER_MONTH * 3 + GAP_PX * 2) setN(3);
      else setN(2);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return n;
}

/** 'single' picks one day (AMERICAN tournaments); 'range' picks a span. */
export type ScheduleDatesMode = 'single' | 'range';

export interface ScheduleDatesSelectorProps {
  /** Default: 'range'. */
  mode?: ScheduleDatesMode;
  value: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  minDate?: Date;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

/**
 * Calendar for schedule-block dates. Emits a `DateRange` in both modes:
 * in 'single' mode `from` and `to` are the same day, so consumers keep
 * a single state shape.
 */
export function ScheduleDatesSelector({
  mode = 'range',
  value,
  onSelect,
  minDate,
  disabled = false,
  isLoading = false,
  className,
}: ScheduleDatesSelectorProps) {
  const from = value?.from;
  const to = value?.to;
  const isSingle = mode === 'single';
  const containerRef = useRef<HTMLDivElement>(null);
  const numberOfMonths = useNumberOfMonths(containerRef);

  const label = isSingle
    ? 'Seleccionar día del torneo'
    : 'Seleccionar rango de fechas';

  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <Label>{label}</Label>
        <Skeleton className="h-[280px] w-full rounded-md" />
      </div>
    );
  }

  const selectionText = (() => {
    if (!from) return null;
    if (isSingle) return formatDate(from, DATE_WITH_WEEKDAY_FORMAT);
    if (!to) return null;
    if (isSameCalendarDay(from, to)) return formatDate(from, DATE_FORMAT);
    return `${formatDate(from, DATE_FORMAT)} — ${formatDate(to, DATE_FORMAT)}`;
  })();

  const isDateDisabled = (date: Date) => {
    if (disabled) return true;
    if (minDate) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      const m = new Date(minDate);
      m.setHours(0, 0, 0, 0);
      return d < m;
    }
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  const defaultMonth = from ?? minDate ?? new Date();

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        {label}
      </Label>
      {selectionText && (
        <p className="text-sm text-muted-foreground">{selectionText}</p>
      )}
      <div ref={containerRef} className="flex justify-center overflow-hidden">
        {isSingle ? (
          <Calendar
            mode="single"
            selected={from}
            onSelect={(day) => onSelect(day ? { from: day, to: day } : undefined)}
            numberOfMonths={numberOfMonths}
            defaultMonth={defaultMonth}
            className="rounded-md border"
            disabled={isDateDisabled}
          />
        ) : (
          <Calendar
            mode="range"
            selected={value}
            onSelect={(range) => onSelect(normalizeDateRange(range))}
            numberOfMonths={numberOfMonths}
            defaultMonth={defaultMonth}
            className="rounded-md border"
            disabled={isDateDisabled}
          />
        )}
      </div>
    </div>
  );
}
